// src/components/programs/ProgramForm/helpers/payloadBuilder.js

import { calculateProgramTotals } from "./quotationCalculations";

const fillEmptyLanguageFields = (data) => {
    const multiLangFields = ['title', 'description'];
    const filledData = { ...data };

    multiLangFields.forEach(fieldName => {
        const fieldObj = filledData[fieldName];

        if (!fieldObj || typeof fieldObj !== 'object') {
            return;
        }

        const arValue = fieldObj.ar?.trim() || '';
        const enValue = fieldObj.en?.trim() || '';

        const filledFieldObj = {
            ar: arValue,
            en: enValue,
        };

        if (arValue && !enValue) {
            filledFieldObj.en = arValue;
        }

        if (enValue && !arValue) {
            filledFieldObj.ar = enValue;
        }

        filledData[fieldName] = filledFieldObj;
    });

    return filledData;
};

const buildDayProductPayload = (product) => {
    const productPayload = {
        product_id: product.product_snapshot.product_id,
        supplier_id: product.selected_supplier?.id || null,
        order: product.order || 1,
    };

    productPayload.custom_price = product.pricing.custom_price !== null && product.pricing.custom_price !== undefined
        ? parseFloat(product.pricing.custom_price)
        : null;

    productPayload.original_sell_price = parseFloat(
        product.pricing.original_sell_price ||
        product.product_snapshot.original_sell_price ||
        product.product_snapshot.original_price ||
        0
    );

    const pricingUnit = product.product_snapshot?.pricing_unit;
    const customPaxPrices = product.pricing?.custom_pax_prices;
    const isPriceEdited = product.pricing?.is_price_edited;
    const snapshotPaxTypePrices = product.product_snapshot?.pax_type_prices || [];

    if (
        (pricingUnit === 'per_person' || pricingUnit === 'per_person_night') &&
        snapshotPaxTypePrices.length > 0
    ) {
        const customPaxCosts = product.pricing?.custom_pax_costs;
        const hasAnyCustomSell = customPaxPrices && Object.keys(customPaxPrices).length > 0 && isPriceEdited;
        const hasAnyCustomCost = customPaxCosts && Object.keys(customPaxCosts).length > 0;

        if (hasAnyCustomSell || hasAnyCustomCost) {
            productPayload.pax_type_prices = snapshotPaxTypePrices.map(ptp => {
                const paxTypeId = parseInt(ptp.pax_type_id);
                const customPrice = hasAnyCustomSell ? customPaxPrices[paxTypeId] : undefined;
                const customCost = customPaxCosts?.[paxTypeId];

                const hasCustomPrice = customPrice !== undefined && customPrice !== null && !isNaN(parseFloat(customPrice));
                const hasCustomCost = customCost !== undefined && customCost !== null && !isNaN(parseFloat(customCost));

                return {
                    pax_type_id: paxTypeId,
                    price: hasCustomPrice ? parseFloat(customPrice) : parseFloat(ptp.price) || 0,
                    ...(hasCustomCost && { cost_price: parseFloat(customCost) }),
                };
            });
            productPayload.custom_price = null;
        }
    }

    if (
        pricingUnit !== 'per_person' &&
        isPriceEdited &&
        product.pricing?.custom_cost_price != null
    ) {
        productPayload.custom_cost_price = parseFloat(product.pricing.custom_cost_price);
    }

    if (product.pricing.discount_type && product.pricing.discount_type !== 'zero') {
        productPayload.discount_type = product.pricing.discount_type;
        productPayload.discount = parseFloat(product.pricing.discount_value) || 0;
    }

    if (product.dynamic_fields) {
        if (product.dynamic_fields.time) {
            productPayload.time = product.dynamic_fields.time;
        }

        if (product.dynamic_fields.pickup_time) {
            productPayload.pickup_time = product.dynamic_fields.pickup_time;
        }

        if (product.dynamic_fields.city_id) {
            productPayload.city_id = parseInt(product.dynamic_fields.city_id);
        }

        if (product.dynamic_fields.flight_details) {
            const flightDetails = product.dynamic_fields.flight_details;
            const flightPayload = {};
            if (flightDetails.airline_id) flightPayload.airline_id = parseInt(flightDetails.airline_id);
            if (flightDetails.departure_airport_id) flightPayload.departure_airport_id = parseInt(flightDetails.departure_airport_id);
            if (flightDetails.arrival_airport_id) flightPayload.arrival_airport_id = parseInt(flightDetails.arrival_airport_id);
            if (flightDetails.departure_date) flightPayload.departure_date = flightDetails.departure_date;
            if (flightDetails.arrival_date) flightPayload.arrival_date = flightDetails.arrival_date;
            if (flightDetails.departure_time) flightPayload.departure_time = flightDetails.departure_time;
            if (flightDetails.arrival_time) flightPayload.arrival_time = flightDetails.arrival_time;
            if (flightDetails.flight_number) flightPayload.flight_number = flightDetails.flight_number;
            if (flightDetails.pnr) flightPayload.pnr = flightDetails.pnr;
            if (flightDetails.baggage_weight !== null && flightDetails.baggage_weight !== undefined && flightDetails.baggage_weight !== "") {
                flightPayload.baggage_weight = parseInt(flightDetails.baggage_weight) || 0;
            }
            if (flightDetails.baggage_unit) {
                flightPayload.baggage_unit = flightDetails.baggage_unit;
            }
            if (Object.keys(flightPayload).length > 0) {
                productPayload.flight_details = flightPayload;
            }
        }

        if (product.dynamic_fields.units !== null && product.dynamic_fields.units !== undefined) {
            productPayload.units = parseInt(product.dynamic_fields.units);
        }

        if (product.dynamic_fields.nights !== null && product.dynamic_fields.nights !== undefined) {
            productPayload.nights = parseInt(product.dynamic_fields.nights);
        }
    }

    return productPayload;
};

const getExtrasForHotelStay = (hotel, itinerary) => {
    // NEW format: room_night_daily — extras on hotel.extras[]
    if (hotel.room_stays?.length > 0) {
        return (hotel.extras || []).map(extra => ({
            hotel_extra_id: extra.hotel_extra_id ? parseInt(extra.hotel_extra_id) : null,
            pricing_basis: extra.pricing_basis || 'once',
            quantity: parseInt(extra.quantity) || 1,
            custom_sell_price: parseFloat(extra.custom_sell_price) || 0,
            custom_cost_price: parseFloat(extra.custom_cost_price) || 0,
            applicable_dates: extra.applicable_dates || [],
        }));
    }

    // LEGACY format: extras from itinerary days within date range
    if (!itinerary || !hotel.check_in_date || !hotel.check_out_date) return [];

    const extras = [];
    itinerary.forEach(day => {
        if (!day.date || !day.extras || day.extras.length === 0) return;
        if (day.date >= hotel.check_in_date && day.date < hotel.check_out_date) {
            day.extras.forEach(extra => {
                const extraPayload = {
                    hotel_extra_id: extra.hotel_extra_id ? parseInt(extra.hotel_extra_id) : null,
                    pricing_basis: extra.pricing_basis || 'once',
                    quantity: parseInt(extra.quantity) || 1,
                    custom_sell_price: parseFloat(extra.custom_sell_price) || 0,
                    custom_cost_price: parseFloat(extra.custom_cost_price) || 0,
                };
                if (extra.days_count) {
                    extraPayload.days_count = parseInt(extra.days_count);
                }
                extras.push(extraPayload);
            });
        }
    });

    return extras;
};

/**
 * Build API payload from form data
 */
export const buildPayload = (formData) => {
    const processedFormData = fillEmptyLanguageFields(formData);

    // Build days from itinerary products + hotel products
    const dayMap = {};

    // 1. Process itinerary products
    processedFormData.itinerary.forEach((day) => {
        if (!day.date) return;

        if (!dayMap[day.date]) {
            dayMap[day.date] = {
                day_name: day.day_name || { ar: '', en: '' },
                date: day.date,
                products: [],
            };
        }

        day.products.forEach(product => {
            dayMap[day.date].products.push(buildDayProductPayload(product));
        });
    });

    // 2. Convert hotel stays to products on their first day
    const hotels = processedFormData.hotels || [];
    hotels.forEach((hotel) => {
        if (!hotel.check_in_date || !hotel.product_snapshot) return;

        const isRoomNightDaily = hotel.room_stays?.length > 0;

        // Common hotel details fields
        const hotelDetails = {
            hotel_name: hotel.hotel_name || '',
            country_id: hotel.country_id ? parseInt(hotel.country_id) : null,
            city_id: hotel.city_id ? parseInt(hotel.city_id) : null,
            check_in_time: hotel.check_in_time || '',
            check_out_time: hotel.check_out_time || '',
            meal_plan_id: hotel.meal_plan_id ? parseInt(hotel.meal_plan_id) : null,
            hotel_rating_id: hotel.hotel_rating_id ? parseInt(hotel.hotel_rating_id) : null,
        };

        if (isRoomNightDaily) {
            // NEW format: room_night_daily
            hotelDetails.pricing_mode = 'room_night_daily';
            hotelDetails.room_stays = hotel.room_stays.map((rs, i) => {
                const customSell = hotel.pricing?.custom_room_prices?.[i];
                const customCost = hotel.pricing?.custom_room_costs?.[i];
                const sellPrice = customSell !== undefined && customSell !== null ? parseFloat(customSell) : rs.custom_sell_price;
                const costPrice = customCost !== undefined && customCost !== null ? parseFloat(customCost) : rs.custom_cost_price;
                return {
                    date: rs.date,
                    room_category_id: rs.room_category_id ? parseInt(rs.room_category_id) : null,
                    rooms_count: rs.rooms_count || 1,
                    custom_sell_price: sellPrice ?? null,
                    custom_cost_price: costPrice ?? null,
                };
            });

            // Extras are collected from each room_stay's own extras array
            const extras = getExtrasForHotelStay(hotel);
            if (extras.length > 0) {
                hotelDetails.extras = extras;
            }
        } else {
            // LEGACY format: per_person_night
            hotelDetails.pricing_mode = 'legacy_per_person_night';
            hotelDetails.room_category_id = hotel.room_category_id ? parseInt(hotel.room_category_id) : null;

            // Add extras from all days within this hotel stay
            const extras = getExtrasForHotelStay(hotel, processedFormData.itinerary);
            if (extras.length > 0) {
                hotelDetails.extras = extras;
            }
        }

        const hotelPayload = {
            product_id: hotel.product_snapshot.product_id,
            supplier_id: hotel.selected_supplier?.id || null,
            order: 999,
            custom_price: null,
            original_sell_price: isRoomNightDaily
                ? 0
                : parseFloat(hotel.product_snapshot.original_sell_price || hotel.product_snapshot.original_price || 0),
            hotel_details: hotelDetails,
        };

        if (isRoomNightDaily) {
            // nights = number of unique room_stay dates
            const uniqueDates = new Set(hotel.room_stays.map(rs => rs.date).filter(Boolean));
            hotelPayload.nights = uniqueDates.size;
        }

        if (!isRoomNightDaily) {
            hotelPayload.units = hotel.rooms || 1;
            hotelPayload.nights = hotel.nights || 1;

            // Add custom pax_type_prices if custom prices are defined
            const snapshotPaxPrices = hotel.product_snapshot?.pax_type_prices || [];
            const customPaxPrices = hotel.pricing?.custom_pax_prices;
            const customPaxCosts = hotel.pricing?.custom_pax_costs;
            const hasAnyCustomSell = customPaxPrices && Object.keys(customPaxPrices).length > 0;
            const hasAnyCustomCost = customPaxCosts && Object.keys(customPaxCosts).length > 0;

            if ((hasAnyCustomSell || hasAnyCustomCost) && snapshotPaxPrices.length > 0) {
                hotelPayload.pax_type_prices = snapshotPaxPrices.map(ptp => {
                    const paxTypeId = parseInt(ptp.pax_type_id);
                    const hasCustomPrice = customPaxPrices && customPaxPrices.hasOwnProperty(paxTypeId);
                    const hasCustomCost = customPaxCosts && customPaxCosts.hasOwnProperty(paxTypeId);
                    return {
                        pax_type_id: paxTypeId,
                        price: hasCustomPrice ? (parseFloat(customPaxPrices[paxTypeId]) || 0) : (parseFloat(ptp.price) || 0),
                        ...(hasCustomCost && { cost_price: parseFloat(customPaxCosts[paxTypeId]) || 0 }),
                    };
                });
            }
        }

        if (!dayMap[hotel.check_in_date]) {
            dayMap[hotel.check_in_date] = {
                day_name: { ar: '', en: '' },
                date: hotel.check_in_date,
                products: [],
            };
        }

        dayMap[hotel.check_in_date].products.push(hotelPayload);
    });

    // Convert dayMap to array sorted by date
    const days = Object.values(dayMap)
        .sort((a, b) => a.date.localeCompare(b.date))
        .filter(day => day.products.length > 0);

    const totalPax = processedFormData.pax_types.reduce((sum, pt) => sum + (parseInt(pt.count) || 0), 0);
    const programTotals = calculateProgramTotals(processedFormData);

    const payload = {
        title: processedFormData.title,
        description: processedFormData.description,
        agent_id: processedFormData.agent_id,
        city_ids: (processedFormData.city_ids || []).map(id => parseInt(id)).filter(id => !isNaN(id)),
        cities: "",
        start_date: processedFormData.start_date,
        end_date: processedFormData.end_date,
        durations: parseInt(processedFormData.durations_days) || 0,
        nights: parseInt(processedFormData.durations_nights) || 0,
        show_date_in_template: !!processedFormData.show_date_in_template,
        number_of_pax: totalPax,
        price: parseFloat(programTotals.final_selling_price.toFixed(2)),
        pax_types: processedFormData.pax_types
            .filter(pt => pt.pax_type_id && parseInt(pt.count) > 0)
            .map(pt => ({
                pax_type_id: parseInt(pt.pax_type_id),
                quantity: parseInt(pt.count),
            })),
        days: days,
    };

    if (processedFormData.cover_image) {
        payload.cover_image = processedFormData.cover_image;
    }

    if (processedFormData.global_discount.type && processedFormData.global_discount.type !== 'zero') {
        payload.discount_type = processedFormData.global_discount.type;
        payload.discount = parseFloat(processedFormData.global_discount.value) || 0;
        payload.discount_target = processedFormData.global_discount.target || 'sell';
    } else {
        payload.discount_type = 'zero';
        payload.discount = 0;
        payload.discount_target = 'sell';
    }

    return payload;
};
